import React, { useRef, useEffect, useMemo } from 'react';
import { Position, TabletopItemType } from '../types';
import useDragDetection from '@/hooks/useDragDetection';
import { selectionGlowFilter } from '@/utils/colorTransforms';

interface TabletopItemProps {
  id: string;
  type: TabletopItemType;
  position: Position;
  rotation: number;
  width: number;
  height: number;
  isSelected: boolean;
  isHighlighted?: boolean;
  isDragging?: boolean;
  onSelect: (id: string, type: TabletopItemType, event: React.MouseEvent) => void;
  onDragStart?: (id: string, type: TabletopItemType, event: React.MouseEvent) => void;
  onMouseEnter?: (id: string, type: TabletopItemType) => void;
  onMouseLeave?: (id: string, type: TabletopItemType) => void;
  className?: string;
  children: React.ReactNode;
  zIndex?: number;
  selectionColor?: string; // Hex color for selection ring
}

export default function TabletopItem({
  id,
  type,
  position,
  rotation,
  width,
  height,
  isSelected,
  isHighlighted = false,
  isDragging = false,
  onSelect,
  onDragStart,
  onMouseEnter,
  onMouseLeave,
  className = '',
  children,
  zIndex,
  selectionColor
}: TabletopItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);
  
  // Use the drag detection hook
  const {
    isDragging: isInternalDragging,
    handleMouseDown: trackMouseDown,
    handleMouseMove: detectDrag,
    handleMouseUp: endDrag,
    handleClick: checkDragging
  } = useDragDetection();

  // Apply position using direct DOM manipulation for smoother animations
  useEffect(() => {
    if (!itemRef.current) return;
    
    itemRef.current.style.transform = `translate3d(${position.x}px, ${position.y}px, 0) rotate(${rotation}deg)`;
  }, [position.x, position.y, rotation]);

  // Handle click to select
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // First, check if we were dragging using our hook
    if (!checkDragging(e)) {
      return; // The hook handled it as a drag, so exit
    }
    
    // If we got here, it's a legitimate click (not a drag)
    onSelect(id, type, e);
  };

  // Handle mouse down for drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackMouseDown(e);
    
    // Select this item immediately
    onSelect(id, type, e);
    
    // Start dragging if handler is provided
    if (onDragStart) {
      onDragStart(id, type, e);
    }
  };

  const isDeck = type === 'deck';
  const isRound = type === 'token' || type === 'coin';

  // Special styling for decks to show stacked cards
  const containerClass = isDeck
    ? 'overflow-visible' // Allow overflow for decks to show stacked cards
    : 'overflow-hidden'; // Keep other items clipped

  const shapeClass = isRound ? 'rounded-full' : 'rounded-md';

  // Selection styles - for decks we need to ensure the selection ring is large enough
  const selectionClass = isSelected
    ? `ring-4 ring-offset-2 ring-offset-transparent dark:ring-offset-transparent ${
        selectionColor ? '' : 'ring-indigo-300/90 dark:ring-indigo-400/80 drop-shadow-[0_0_16px_rgba(129,140,248,0.45)] saturate-125'
      }`
    : '';

  // Custom selection style when color is provided
  const customSelectionStyle = useMemo(() => {
    if (!isSelected || !selectionColor) {
      return undefined;
    }

    const filter = selectionGlowFilter(selectionColor);

    return {
      '--tw-ring-opacity': 1,
      '--tw-ring-color': selectionColor,
      ...(filter ? { filter } : {})
    } as React.CSSProperties;
  }, [isSelected, selectionColor]);

  // Highlight styles
  const highlightClass = isHighlighted
    ? (type === 'deck' ? 'ring-2 ring-green-500 ring-opacity-70 ring-offset-0' : 'ring-2 ring-green-500 ring-opacity-70')
    : '';

  const isTableProp = type === 'dice' || type === 'token' || type === 'coin';
  const restingZIndex = isTableProp ? 1000 : 10;
  const highlightedZIndex = isTableProp ? 1025 : 25;
  const draggingZIndex = isTableProp ? 1080 : 80;
  const baseZIndex = isDragging ? draggingZIndex : isHighlighted ? highlightedZIndex : restingZIndex;
  const computedZIndex =
    typeof zIndex === 'number'
      ? (isDragging ? Math.max(zIndex, baseZIndex) : zIndex)
      : baseZIndex;

  return (
    <div
      ref={itemRef}
      data-table-card-id={type === 'card' ? id : undefined}
      className={`absolute ${shapeClass} cursor-pointer select-none
        ${containerClass}
        ${selectionClass}
        ${highlightClass}
        ${isInternalDragging ? 'shadow-xl' : isSelected ? 'shadow-2xl' : 'shadow-lg'}
        ${className}`
      }
      style={{
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate3d(${position.x}px, ${position.y}px, 0) rotate(${rotation}deg)`,
        transformOrigin: 'center',
        willChange: 'transform',
        zIndex: computedZIndex,
        pointerEvents: 'auto',
        ...customSelectionStyle
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={detectDrag}
      onMouseUp={endDrag}
      onMouseEnter={() => onMouseEnter?.(id, type)}
      onMouseLeave={() => onMouseLeave?.(id, type)}
      onClick={handleClick}
    >
      {children}
    </div>
  );
} 
